/**
 * What you can do where you are standing, and why not.
 *
 * Design pillar 4 says everything answers. M0-06 made everything *examinable*;
 * this makes everything **actionable**, and — more importantly — makes the list
 * of what is actionable a thing the code can be asked for rather than a thing
 * each screen reimplements. Before this module the client knew about `forage`
 * because `game.ts` had a hand-written check for a flora patch, and about the
 * camp verbs because `camp.ts` in the UI had a second one. Two hand-written
 * lists, each a milestone behind the rules they were guessing at.
 *
 * ## The registry does not decide anything
 *
 * That is the whole design. Every verb in the game already had a *fault
 * function* — `forageFault`, `buildFault`, `kindleFault`, `butcherFault` — that
 * answers "why can this actor not do this here, right now" with a sentence or
 * `null`. Those are the rules, they are what each intent handler's `check`
 * calls, and they stay exactly where they are.
 *
 * What this module adds is a **table of them**. `affordancesAt` walks the
 * registry and calls each verb's own fault function, so the menu a player sees
 * and the rule the server enforces cannot disagree: they are the same function,
 * called twice. A registry that carried its own copy of the legality would be a
 * second list to keep in step, which is precisely the failure this ticket
 * exists to prevent.
 *
 * The one thing declared *here* rather than in the verb is the **capability
 * requirement**, because it is the same question for every verb — how much of a
 * body does this take — and answering it in nine places is how a one-handed
 * character ends up able to butcher a stag but not to cook it. Each verb's
 * fault function calls `capabilityFault` against its own entry, so the
 * requirement is declared once and enforced by the rules rather than by the UI.
 */
import { deriveCapabilities } from './body.js';
import { kitOf } from './fighter.js';
/**
 * Work that takes a whole working hand and some dexterity in it — a knife, a
 * flint, a needle. `fineWork` is the best *usable* hand (M1-01), so this is
 * exactly "you have one hand you can still do things with".
 *
 * The floor is 0.5 rather than something above zero because `fineWork` degrades
 * smoothly with impairment and a hand at a fifth of its strength is not a hand
 * you skin a deer with. A broken arm is `BROKEN_FACTOR`, which lands below this
 * — which is the intent: a broken arm stops you butchering.
 */
const HANDS = { capability: 'fineWork', atLeast: 0.5 };
/**
 * Work that takes a hand but not a careful one — putting a log on a fire,
 * taking hold of a body and pulling. A weaker floor, and honest about it: you
 * can drag somebody with a hand you could not stitch with.
 */
const ONE_HAND = { capability: 'fineWork', atLeast: 0.2 };
/** Work that takes legs — there is none in the MVP yet, and the seam is here. */
const LEGS = { capability: 'mobility', atLeast: 0.3 };
const requirement = (base, reason) => ({
    ...base,
    reason,
});
/**
 * Every verb in the game that a player can choose, in the order a menu shows
 * them.
 *
 * Ordered by how often they are wanted, with one exception inherited from
 * M2-07: the destructive verb is last, and never near the finger that just
 * opened the menu.
 *
 * `move`, `attack` and `guard` are deliberately absent. They are not menu
 * verbs — they have keys and a mouse of their own, and putting "walk" in a
 * context menu would be a list with one useless entry at the top of it.
 */
export const AFFORDANCES = [
    {
        verb: 'forage',
        label: 'gather',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot pick anything with the hands you have'),
        channelTicks: 60,
    },
    {
        verb: 'build',
        label: 'lay a fire',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot lay a fire one-handed'),
        channelTicks: 150,
    },
    {
        verb: 'kindle',
        label: 'strike a light',
        target: 'self',
        requires: requirement(HANDS, 'you cannot work a flint with the hands you have'),
        channelTicks: 0,
    },
    {
        verb: 'feed',
        label: 'feed the fire',
        target: 'ground',
        requires: requirement(ONE_HAND, 'you have no hand free to put wood on'),
        channelTicks: 0,
    },
    {
        verb: 'cook',
        label: 'cook',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot cook with the hands you have'),
        channelTicks: 120,
    },
    {
        verb: 'butcher',
        label: 'butcher',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot hold a knife steady enough to butcher'),
        channelTicks: 200,
    },
    // RC2-04's reward ladder, against the hazel deadfall. `salvage` takes the tool
    // cached in it — a `ONE_HAND` job, taking hold of something — and is instant.
    // `chop` fells the wood, and asks for the whole of both hands: swinging an axe
    // to place a blow is not work for the arm you cannot close a fist with, the
    // same gate `amputate` reads.
    {
        verb: 'salvage',
        label: 'take the axe',
        target: 'ground',
        requires: requirement(ONE_HAND, 'you cannot take hold of it with the hands you have'),
        channelTicks: 0,
    },
    {
        verb: 'chop',
        label: 'chop',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot swing an axe with the hands you have'),
        channelTicks: 300,
    },
    {
        verb: 'eat',
        label: 'eat',
        target: 'self',
        requires: requirement(ONE_HAND, 'you cannot get anything to your mouth'),
        channelTicks: 0,
    },
    // M4-03's preparations, beside the fire verbs they belong with. `HANDS`
    // rather than `ONE_HAND`: crushing a poultice into cloth is knife-and-needle
    // work, and a man who cannot butcher cannot make one either.
    {
        verb: 'prepare',
        label: 'make up',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot work a poultice with the hands you have'),
        channelTicks: 180,
    },
    {
        verb: 'rest',
        label: 'lie down',
        target: 'ground',
        // Nothing. Lying down is the one verb a man with no working limbs must
        // always have, and a requirement here would be a rule that traps somebody
        // upright — the interface killing them, which M3-06 already refused once.
        requires: null,
        channelTicks: 0,
    },
    // M4's medicine, as it applies to *yourself*. These are `self` verbs — the
    // menu offers them without a target because the target is you. The same verbs
    // reach a fallen friend through the interact menu (M2-07's down-body path),
    // whose legality the intent handlers check directly; a body entry here as well
    // would put the same verb on two menus with two targets, which is a bug the
    // one-fault-per-verb registry cannot express. Suture asks for a whole steady
    // hand (`HANDS`) where the rest ask only for a working one — the capability
    // seam the ticket wants shown: a bandaged hand can still press a wound but
    // cannot sew one.
    {
        verb: 'staunch',
        label: 'staunch',
        target: 'self',
        requires: requirement(ONE_HAND, 'you cannot press a wound closed with the hands you have'),
        channelTicks: 50,
    },
    {
        verb: 'bandage',
        label: 'bind',
        target: 'self',
        requires: requirement(ONE_HAND, 'you cannot bind a wound with the hands you have'),
        channelTicks: 60,
    },
    {
        verb: 'clean',
        label: 'clean',
        target: 'self',
        requires: requirement(ONE_HAND, 'you cannot wash a wound with the hands you have'),
        channelTicks: 150,
    },
    {
        verb: 'suture',
        label: 'stitch',
        target: 'self',
        requires: requirement(HANDS, 'you cannot hold a needle steady enough to stitch'),
        channelTicks: 250,
    },
    {
        verb: 'cauterize',
        label: 'sear',
        target: 'self',
        requires: requirement(ONE_HAND, 'you cannot hold an iron to the wound'),
        channelTicks: 80,
    },
    // M4-05's surgery, at the end of the ladder it belongs to. Both are `self`
    // verbs like the treatments above and reach a fallen body the same way. Both
    // ask for `HANDS`: digging a splinter out of somebody's thigh is needle work,
    // and swinging an axe at a man's leg with an eye to where it lands is not the
    // job for the arm you cannot close a fist with.
    {
        verb: 'extract',
        label: 'dig out',
        target: 'self',
        requires: requirement(HANDS, 'you cannot hold a blade steady enough to dig for it'),
        channelTicks: 200,
    },
    {
        verb: 'amputate',
        label: 'take it off',
        target: 'self',
        requires: requirement(HANDS, 'you cannot hold an axe well enough to place the blow'),
        channelTicks: 450,
    },
    // M4-07. Burial and its opposite. Ground verbs: they work on a place, like
    // laying a fire, and both ask for the whole of both hands — you dig a grave
    // with a spade, and the man with one arm has to ask somebody.
    {
        verb: 'bury',
        label: 'bury',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot dig with the hands you have'),
        channelTicks: 400,
    },
    {
        verb: 'dig',
        label: 'dig up',
        target: 'ground',
        requires: requirement(HANDS, 'you cannot dig with the hands you have'),
        channelTicks: 250,
    },
    // M4-08. Talking to whoever lives here. A `body` verb — it names somebody
    // adjacent — and the only one on this list that asks nothing of your hands:
    // a man with no arms left can still ask the healer for help, and a rule that
    // said otherwise would be the interface killing somebody again.
    {
        verb: 'talk',
        label: 'talk to',
        target: 'body',
        requires: null,
        channelTicks: 0,
    },
    {
        verb: 'loot',
        label: 'rob',
        target: 'body',
        requires: requirement(ONE_HAND, 'you cannot take hold of anything'),
        channelTicks: 100,
    },
    {
        verb: 'drag',
        label: 'drag',
        target: 'body',
        requires: requirement(LEGS, 'you cannot get the purchase to drag anybody'),
        channelTicks: 0,
    },
    // M4-06. Carrying asks for legs like dragging does, and `burdenFault` asks
    // for more on top (the 0.6 mobility floor) — the registry states the coarse
    // requirement and the verb states its own, exactly as `suture` does.
    {
        verb: 'carry',
        label: 'carry',
        target: 'body',
        requires: requirement(LEGS, 'you cannot get anybody up onto your shoulder'),
        channelTicks: 0,
    },
    {
        verb: 'release',
        label: 'put down',
        target: 'self',
        // Nothing, and deliberately — like `rest`. A verb whose whole job is to
        // stop doing something must never be gated on being well enough to do it.
        requires: null,
        channelTicks: 0,
    },
    {
        verb: 'execute',
        label: 'finish',
        target: 'body',
        requires: requirement(ONE_HAND, 'you cannot hold a blade to anybody'),
        channelTicks: 100,
    },
];
const BY_VERB = new Map(AFFORDANCES.map((entry) => [entry.verb, entry]));
export function affordanceFor(verb) {
    return BY_VERB.get(verb) ?? null;
}
/**
 * Whether this body can meet a verb's demand, or the sentence saying it cannot.
 *
 * Called from inside each verb's own fault function rather than from the menu,
 * which is the difference between a rule and a suggestion: a client that
 * decided to send `butcher` anyway gets the same refusal, because the server
 * asks the same question.
 *
 * A combatant that cannot be resolved against the catalogue is *permitted*, for
 * the reason `isHelpless` refuses to declare a body down without a kit: failing
 * towards leaving the player in control of themselves is the safe direction,
 * and a content-loading mistake should not silently forbid everybody from
 * eating.
 */
export function capabilityFault(catalogue, combatant, verb) {
    const affordance = BY_VERB.get(verb);
    if (!affordance?.requires)
        return null;
    if (!combatant)
        return null;
    const kit = kitOf(catalogue, combatant);
    if (!kit)
        return null;
    return meets(kit.plan, combatant, affordance.requires) ? null : affordance.requires.reason;
}
function meets(plan, combatant, required) {
    const capabilities = deriveCapabilities(plan, combatant.vitals.body);
    return capabilities[required.capability] >= required.atLeast;
}
/**
 * Every verb, with whether this actor may use it where they stand.
 *
 * Returns the *whole* list rather than only what is possible, because a menu
 * that hides what you cannot do teaches nobody anything: the reason a verb is
 * greyed out — "you have nothing to burn", "there is a fire here already" — is
 * the game explaining its own rules at the exact moment somebody wants to know
 * them. Qud's lesson, and M3-03's playtest defect in a different coat.
 *
 * `body` verbs are dropped entirely when there is nobody to use them on, since
 * "rob" with nobody to rob is not a greyed-out option, it is a category error.
 */
export function affordancesAt(context, faults, notes = {}) {
    const offered = [];
    for (const affordance of AFFORDANCES) {
        if (affordance.target === 'body' && context.targetId === undefined)
            continue;
        const fault = faults[affordance.verb];
        // A verb with no fault function registered is one nothing can refuse. That
        // is a bug rather than a permission, so it is reported as unavailable
        // rather than silently offering something the server will reject. The
        // registry test in `affordance.test.ts` is what stops it ever shipping.
        const reason = fault ? fault(context) : 'that is not possible here';
        const available = reason === null;
        // A note is only worth computing for a verb you could actually choose — a
        // greyed entry already carries its reason, and a hint under it would be
        // noise. `notes` is sparse: most verbs have nothing to add.
        const note = available ? (notes[affordance.verb]?.(context) ?? null) : null;
        offered.push({ ...affordance, available, reason, note });
    }
    return offered;
}
//# sourceMappingURL=affordance.js.map